import { useState } from "react";
import { Phone, Mail, MapPin, Navigation } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { company } from "@/data/site";
import { useT } from "@/i18n";

export default function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", phone: "", subject: "", message: "" });
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const subject = form.subject || t("contact.formDefaultSubject", { name: form.name || "KYT" });
    const body =
      `${t("contact.bodyName")}: ${form.name}\n` +
      `${t("contact.bodyPhone")}: ${form.phone}\n` +
      `${t("contact.bodySubject")}: ${form.subject}\n\n` +
      `${form.message}`;
    window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const cards = [
    { key: "address", icon: MapPin, title: t("contact.cardAddress"), value: company.address, href: `https://www.google.com/maps?q=${encodeURIComponent(company.mapsQuery)}` },
    { key: "phone", icon: Phone, title: t("contact.cardPhone"), value: company.phone, href: `tel:${company.phoneHref}` },
    { key: "email", icon: Mail, title: t("contact.cardEmail"), value: company.email, href: `mailto:${company.email}` },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader
        title={t("contact.headerTitle")}
        crumb={t("contact.headerTitle")}
        subtitle={t("contact.headerSubtitle")}
        image="/sections/hero-sanayi.jpg"
      />

      {/* INFO */}
      <section className="py-20 md:py-24 bg-paper">
        <div className="container-kyt grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
          {cards.map((c, i) => (
            <Reveal key={c.key} delay={i * 80}>
              <a
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={`contact-card-${c.key}`}
                className="group block border-t border-ink/15 pt-6"
              >
                <div className="flex items-center gap-2.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-slate-400">
                  <c.icon className="h-4 w-4 text-brand-blue" /> {c.title}
                </div>
                <div className="mt-3 text-[15px] text-ink leading-snug group-hover:text-brand-blue transition-colors">{c.value}</div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FORM + MAP */}
      <section className="pb-24 md:pb-32 bg-paper">
        <div className="container-kyt grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="bg-white border border-ink/10 p-8 md:p-12">
              <div className="eyebrow mb-4">{t("contact.writeEyebrow")}</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink">{t("contact.formTitle")}</h2>
              <p className="text-slate-500 text-sm mt-3 font-light">{t("contact.formDesc")}</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input name="name" value={form.name} onChange={onChange} required placeholder={t("contact.phName")} data-testid="form-name" className="w-full border border-ink/15 px-4 py-3.5 text-[15px] outline-none focus:border-ink transition-colors bg-transparent" />
                  <input name="phone" value={form.phone} onChange={onChange} placeholder={t("contact.phPhone")} data-testid="form-phone" className="w-full border border-ink/15 px-4 py-3.5 text-[15px] outline-none focus:border-ink transition-colors bg-transparent" />
                </div>
                <input name="subject" value={form.subject} onChange={onChange} placeholder={t("contact.phSubject")} data-testid="form-subject" className="w-full border border-ink/15 px-4 py-3.5 text-[15px] outline-none focus:border-ink transition-colors bg-transparent" />
                <textarea name="message" value={form.message} onChange={onChange} required rows={5} placeholder={t("contact.phMessage")} data-testid="form-message" className="w-full border border-ink/15 px-4 py-3.5 text-[15px] outline-none focus:border-ink transition-colors resize-none bg-transparent" />
                <button type="submit" data-testid="form-submit" className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-ink text-white px-6 py-4 font-medium transition-all hover:bg-brand-blue">
                  <Mail className="h-5 w-5" /> {t("contact.formSubmit")}
                </button>
              </form>
              <div className="mt-7 font-mono text-[12px] tracking-wide text-slate-400 border-t border-ink/10 pt-5">
                {t("contact.hours")}
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative h-full min-h-[460px] overflow-hidden bg-slate-100 border border-ink/10 group" data-testid="contact-map">
              <img
                src="/sections/map.jpg"
                alt={`${company.addressShort} — ${t("contact.mapTitle")}`}
                loading="lazy"
                className="h-full w-full object-cover min-h-[460px] transition-transform duration-[1200ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <div className="flex items-start gap-2.5 text-white/85 text-[14px] leading-snug mb-4">
                  <MapPin className="h-4.5 w-4.5 text-brand-electric mt-0.5 shrink-0" />
                  <span>{company.address}</span>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${encodeURIComponent(company.mapsQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions"
                  className="inline-flex items-center gap-2.5 rounded-full bg-white text-navy px-6 py-3 text-[14px] font-semibold tracking-wide transition-all hover:bg-brand-blue hover:text-white hover:-translate-y-0.5"
                >
                  <Navigation className="h-4.5 w-4.5" /> {t("contact.directions")}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
